import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { CardDivider } from '@/components/atoms/Card';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { renderTrendIcon } from '@/components/atoms/TrendIcon/TrendIconUtils';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import { localizedDateFormat } from '@/utils/date';

import { buildLiceStatusRows, type LiceType } from './liceStatusRows';
import type { LiceInMin200Fish } from './types';

import styles from './LiceStatus.module.scss';

/** Where the arrow beside each count leads, relative to the cage's lice page. */
const DETAIL_ROUTE: Record<LiceType, string> = {
    female: 'female',
    ovigerousFemale: 'ovigerousfemale',
    movingStage: 'mobile',
    caligusElongatus: 'caligus',
};

const SKELETON_FOOTER_WIDTH = 190;
const SKELETON_FOOTER_HEIGHT = 12;

export interface LiceStatusProps {
    liceInMin200Fish?: LiceInMin200Fish | null;
    licePageUrl?: string;
    hasLiceLicense: boolean;
    isChileanLice: boolean;
    /**
     * Draws placeholders instead of the counts.
     *
     * Not cosmetic. With no snapshot yet the block prints `-` under every count and explains it
     * with "Not enough measurements" — a claim about the pen, made while the request is still
     * in flight. A reader cannot otherwise tell a cage that is loading from one that genuinely
     * counted too few fish.
     */
    isLoading?: boolean;
}

/**
 * The three-day lice averages for a cage, one row per stage.
 *
 * Deliberately card-less: the pages that use it supply their own `ContentBlock`, which is how
 * the cage dashboard nests it beside other status blocks.
 */
export default function LiceStatus(props: LiceStatusProps) {
    const {
        liceInMin200Fish,
        licePageUrl = '',
        hasLiceLicense,
        isChileanLice,
        isLoading = false,
    } = props;
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    // Built while loading too: the labels do not depend on the data, and drawing the same rows
    // keeps the panel from resizing the moment the counts land.
    const rows = buildLiceStatusRows({
        liceInMin200Fish,
        isChileanLice,
        locale: i18n.language,
        t,
    });

    const lastMeasurement = liceInMin200Fish?.day
        ? `${t('common.labels.lastMeasurementDay')}: ${localizedDateFormat({
              dateString: liceInMin200Fish.day,
              onlydate: true,
              locale: i18n.language,
          })}`
        : '';

    return (
        <div className={styles.liceStatus}>
            {rows.map((row) => (
                <Fragment key={row.liceType}>
                    <MetricSummaryRow
                        className={styles.row}
                        label={row.label}
                        value={row.value}
                        trend={renderTrendIcon(row.trend)}
                        isLoading={isLoading}
                        tooltipDisabled={isLoading}
                        tooltip={
                            row.tooltip.length > 0 ? (
                                <div className={styles.tooltipBlock}>
                                    {row.tooltip.map((paragraph) => (
                                        <p key={paragraph}>{paragraph}</p>
                                    ))}
                                </div>
                            ) : undefined
                        }
                        action={
                            hasLiceLicense && (
                                <Button
                                    onClick={() =>
                                        navigate(`${licePageUrl}/${DETAIL_ROUTE[row.liceType]}`)
                                    }
                                    variant={'tertiary'}
                                    trackingId={`lice_status_view_${DETAIL_ROUTE[row.liceType]}`}
                                    iconOnly
                                    aria-label={t('common.actions.view')}
                                    icon={<SvgIcon color="currentColor" icon={ArrowRightLine} />}
                                />
                            )
                        }
                    />
                    <CardDivider />
                </Fragment>
            ))}
            {isLoading ? (
                <SkeletonBox width={SKELETON_FOOTER_WIDTH} height={SKELETON_FOOTER_HEIGHT} />
            ) : (
                lastMeasurement && <p className={styles.footer}>{lastMeasurement}</p>
            )}
        </div>
    );
}
